import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { setCachedSecondaryEmailsCount } from 'composables/useSecondaryEmailsCountCache.js'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

// The "Email addresses" settings card: the account's linked emails plus what
// this session is allowed to do with them (GET /api/v1/user/secondary_emails).
export const useSecondaryEmailsStore = defineStore('secondary_emails', {
  state: () => ({
    data: null,
    fetchedAt: null,
    loading: false,
    error: null,
    // The account page renders the card twice, once in its mobile block and
    // once in its desktop block. Both mount even though only one is visible,
    // so without this they would fire the same request in parallel.
    inFlight: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid && this.data) return
      await this.fetchData()
    },

    async fetchData() {
      if (this.inFlight) return this.inFlight

      this.loading = true
      this.error = null

      this.inFlight = (async () => {
        try {
          const { axiosRequest } = useRequest()
          const res = await axiosRequest('GET', '/api/v1/user/secondary_emails', {}, false)
          if (!res.js_error && res.response.success) {
            this.setData(res.response.data)
          } else {
            this.error = 'Failed to load.'
          }
        } catch {
          this.error = 'Something went wrong.'
        } finally {
          this.loading = false
          this.inFlight = null
        }
      })()

      return this.inFlight
    },

    // The whole payload, as returned by index and by removing an email.
    setData(data) {
      this.data = data
      this.fetchedAt = Date.now()
      setCachedSecondaryEmailsCount(data.emails.length)
    },

    // One row, as returned by adding or resending. Replaces the row with the
    // same id, or appends a new one.
    upsertEmail(row) {
      if (!this.data) return

      const index = this.data.emails.findIndex((email) => email.id === row.id)
      if (index === -1) this.data.emails.push(row)
      else this.data.emails.splice(index, 1, row)

      this.fetchedAt = Date.now()
      setCachedSecondaryEmailsCount(this.data.emails.length)
    },

    invalidate() {
      this.fetchedAt = null
    },
  },
})
